export type BadgeRingSource = 'manual' | 'incoming' | 'outgoing' | 'all'
export type BadgeRingReference = 'badge_value' | 'flow_sum'

export interface ResolvedBadgeRingSegment {
    id: string
    sourceId: string
    name: string
    value: number
    color: string
    enabled: boolean
    role: 'incoming' | 'outgoing' | 'residual' | 'manual'
}

const nodeId = (node: any): string => String(typeof node?.id === 'function' ? node.id() : node?.id ?? '')
const finiteValue = (value: unknown): number | null => Number.isFinite(Number(value)) ? Number(value) : null
const effectiveConnectionValue = (connection: any): number =>
    Number.isFinite(Number(connection?.getEffectiveValue?.()))
        ? Number(connection.getEffectiveValue())
        : finiteValue(connection?.effective_value ?? connection?.value) ?? 0

export function resolveBadgeRing(badge: any, connections: any[]): ResolvedBadgeRingSegment[] {
    const source: BadgeRingSource = ['incoming', 'outgoing', 'all'].includes(badge?.ring_source)
        ? badge.ring_source : 'manual'
    if (source === 'manual') {
        return (Array.isArray(badge?.ring_segments) ? badge.ring_segments : []).map((segment: any) => ({
            ...segment,
            sourceId: String(segment.sourceId ?? segment.id),
            role: 'manual',
        }))
    }

    const badgeId = nodeId(badge)
    const contributions = (Array.isArray(connections) ? connections : []).flatMap(connection => {
        const incoming = String(connection?.to ?? '') === badgeId
        const outgoing = String(connection?.from ?? '') === badgeId
        const selected = source === 'all' ? incoming || outgoing : source === 'incoming' ? incoming : outgoing
        if (!selected) return []
        const sourceId = nodeId(connection)
        const value = effectiveConnectionValue(connection)
        return [{
            id: `connection-${sourceId}`,
            sourceId,
            name: connection?.comment || `Connection ${connection?.haspid ?? sourceId}`,
            value: Math.max(0, value),
            color: connection?.bullet_color || connection?.line_color || '#ffffffff',
            enabled: true,
            role: incoming ? 'incoming' as const : 'outgoing' as const,
        }]
    })

    if (badge?.ring_reference !== 'badge_value') return contributions
    const badgeValue = finiteValue(badge?.value)
    if (badgeValue === null || badgeValue <= 0) return contributions
    const residual = Math.max(0, badgeValue - contributions.reduce((sum, segment) => sum + segment.value, 0))
    if (residual <= 0) return contributions
    return [...contributions, {
        id: 'connection-residual', sourceId: 'residual', name: 'Residual', value: residual,
        color: badge?.border_color || '#ffffffff', enabled: true, role: 'residual',
    }]
}
